import { cloneDeep, intersection } from 'lodash';

import {
  MaudPermissions,
  ProviderAccess,
  ROLE_WITH_EDIT_FAMILY_TASK_PERMISSION,
  UI_ONLY_ROLES,
} from '../constants/permissions.constants';

const buildPermissionsTree = <P extends string, R extends string>(
  rolesState: Record<R, RolesState<P>>,
  userRoles: R[],
  permissions: P[],
): Record<R, RolesState<P>> => {
  const updatedRolesState = cloneDeep(rolesState);

  Object.entries(rolesState).forEach(
    ([roleValue, { permissions: rolePermissions }]: [R, RolesState<P>]) => {
      const userRole = updatedRolesState[roleValue];

      if (userRoles.includes(roleValue)) {
        Object.assign(userRole, { isChecked: true });
      }
      const rolePermissionsValues = Object.keys(rolePermissions);
      intersection(rolePermissionsValues, permissions).forEach(
        (permission: P) => {
          const definedPermission = userRole.permissions[permission];
          if (definedPermission && !definedPermission.isHidden) {
            Object.assign(userRole.permissions[permission], {
              isChecked: true,
            });
          }
        },
      );
    },
  );

  return updatedRolesState;
};

const buildRolesAndPermissions = <A extends string>(
  access: AccessMap<A>,
  domainMap: Record<string, A>,
  rolesState: Record<any, RolesState<any>>,
): [string[], string[]] => {
  const userRoles: string[] = [];
  const userPermissions: string[] = [];

  Object.entries(rolesState).forEach(
    ([userRole, { isChecked, permissions }]: [string, RolesState<any>]) => {
      const roleDomain = userRole.split(':')[0];
      const domain = domainMap[roleDomain];
      if (access[domain]) {
        if (isChecked && !UI_ONLY_ROLES.includes(userRole)) {
          userRoles.push(userRole);
        }
        Object.entries(permissions).forEach(
          ([permission, { isChecked: isPermissionChecked, isHidden }]: [
            string,
            PermissionsState,
          ]) => {
            if (isPermissionChecked && !isHidden) {
              userPermissions.push(permission);
            }
          },
        );
      }
    },
  );

  Object.entries(access).forEach(
    ([accessValue, hasAccess]: [ProviderAccess, boolean]) => {
      if (hasAccess) {
        userPermissions.push(accessValue);
      }
    },
  );

  return [userRoles, userPermissions];
};

export const autoUpdatePermissionBasedOnRole = ({
  hasMaudAccess,
  permissions,
  role,
}: {
  hasMaudAccess: boolean;
  permissions: string[];
  role: string;
}): string[] => {
  /**
   * Edit Family Task Permission should be set automatically
   * based on role - care coordinator, admissions specialist and family experience navigator
   */
  if (hasMaudAccess && ROLE_WITH_EDIT_FAMILY_TASK_PERMISSION.includes(role)) {
    if (!permissions.includes(MaudPermissions.MAUD_EDIT_FAMILY_TASK)) {
      permissions.push(MaudPermissions.MAUD_EDIT_FAMILY_TASK);
    }
  } else {
    return permissions.filter(
      (permission) => permission !== MaudPermissions.MAUD_EDIT_FAMILY_TASK,
    );
  }

  return permissions;
};

const isAccessValid = <A extends string, P extends string, R extends string>(
  access: AccessMap<A>,
  providerAccess: A,
  roles: Record<R, RolesState<P>>,
): boolean =>
  access[providerAccess]
    ? Object.values(roles).reduce<boolean>(
        (acc: boolean, { isChecked }: RolesState<P>) => acc || isChecked,
        false,
      )
    : true;

const createPermissionsMap = (
  permissions: string[],
): Record<MaudPermissions, boolean> => {
  const allPermissions = Object.values(MaudPermissions);
  const permissionsMap = Object.fromEntries(
    allPermissions.map((p) => [p, false]),
  ) as Record<MaudPermissions, boolean>;
  for (const p of new Set(
    permissions.filter((p) => allPermissions.includes(p as MaudPermissions)),
  )) {
    permissionsMap[p] = true;
  }
  return permissionsMap;
};

export {
  buildPermissionsTree,
  buildRolesAndPermissions,
  createPermissionsMap,
  isAccessValid,
};
